import React, { useState } from 'react';

import ScoringMatchCard from './scoring-match-card';
import { filterMatches, matchesOfPlayer } from './scoring-match-query';
import ScoringPlayerMatchesModal from './scoring-player-matches-modal';
import { orderedRuns, runLabel, runsOfMatches, type RunUnit } from './scoring-runs';
import ScoringSearchBox from './scoring-search-box';
import ScoringSectionLine, { MATCH_UNITS, countLabel } from './scoring-section-line';
import ScoringStandings from './scoring-standings';
import TiebreakNote from './tiebreak-note';
import {
  groupKey,
  matchesOf,
  groupFormatLabel,
  usesPointSystem,
  type MatchItem,
  type ScoringGroup,
  type StandingRow,
} from './scoring-types';

interface Props {
  groups: ScoringGroup[];
  matches: MatchItem[];
  live: boolean;
  runUnit?: RunUnit;
}

interface PickedPlayer {
  group: ScoringGroup;
  row: StandingRow;
}

interface GroupProps {
  group: ScoringGroup;
  matches: MatchItem[];
  runUnit: RunUnit;
  onPickPlayer: (row: StandingRow) => void;
}

function matchCard(match: MatchItem): JSX.Element {
  return <ScoringMatchCard key={match.id} match={match} canSubmit={false} showRating />;
}

function RoundScoringGroup({ group, matches, runUnit, onPickPlayer }: GroupProps): JSX.Element {
  const [query, setQuery] = useState('');
  const key = groupKey(group);
  const visibleMatches = filterMatches(matches, query);
  const runs = orderedRuns(runsOfMatches(visibleMatches), runUnit);

  return (
    <div className="round-scoring__group" data-testid={`round-scoring-group-${key}`}>
      <header className="round-scoring__group-head">
        <h3 className="round-scoring__group-name">
          {group.name === null ? 'Mecze kolejki' : `Grupa ${group.name}`}
        </h3>
        <span className="round-scoring__group-aside">
          <span className="round-scoring__group-meta">
            {group.playedCount}/{group.matchCount} meczów · {groupFormatLabel(group)}
          </span>
          <TiebreakNote
            hint={group.tiebreak?.hint}
            steps={group.tiebreak?.steps}
            formatLabel={groupFormatLabel(group)}
            testId={`round-standings-${key}-note`}
          />
        </span>
      </header>

      <ScoringStandings
        rows={group.standings}
        showPoints={usesPointSystem(group)}
        tiebreakHint={group.tiebreak?.hint}
        restingRunPoints={group.restingRunPoints ?? null}
        testId={`round-standings-${key}`}
        onPickPlayer={onPickPlayer}
      />

      <ScoringSectionLine title="Mecze" count={countLabel(matches.length, MATCH_UNITS)}>
        <ScoringSearchBox value={query} onChange={setQuery} />
      </ScoringSectionLine>

      {visibleMatches.length === 0 && query.trim() !== '' && (
        <p className="scoring-no-matches" data-testid="scoring-search-empty">
          Brak meczów dla „{query}".
        </p>
      )}

      <ul className="scoring-matches" data-testid={`round-scoring-matches-${key}`}>
        {runs === null
          ? visibleMatches.map(matchCard)
          : runs.map((run) => (
              <React.Fragment key={run.number}>
                <li className="scoring-run-head" data-testid={`round-scoring-run-${run.number}`}>
                  {runLabel(run, runUnit)}
                </li>
                {run.matches.map(matchCard)}
              </React.Fragment>
            ))}
      </ul>
    </div>
  );
}

export const RoundScoringPanel: React.FC<Props> = ({ groups, matches, live, runUnit = 'runda' }) => {
  const [picked, setPicked] = useState<PickedPlayer | null>(null);
  const playedGroups = groups.filter((group) => group.matchCount > 0);

  if (playedGroups.length === 0) {
    return null;
  }

  return (
    <section className="round-scoring" data-testid="round-scoring-panel">
      <h2 className="round-scoring__title">Tabela i mecze kolejki</h2>
      <p className="scoring-hint">
        {live
          ? 'Na żywo z wpisanych wyników. „Klas." to punkty do klasyfikacji sezonu za miejsce w grupie, a przy meczu widać szacunkową zmianę rankingu.'
          : '„Klas." to punkty do klasyfikacji sezonu za miejsce w grupie, a przy meczu widać zmianę rankingu.'}
      </p>
      <p className="scoring-hint">
        Kliknij zawodnika w tabeli — zobaczysz jego ranking i wszystkie mecze w tej grupie.
      </p>

      {playedGroups.map((group) => (
        <RoundScoringGroup
          key={groupKey(group)}
          group={group}
          matches={matchesOf(matches, group)}
          runUnit={runUnit}
          onPickPlayer={(row) => setPicked({ group, row })}
        />
      ))}

      {picked !== null && (
        <ScoringPlayerMatchesModal
          player={picked.row}
          matches={matchesOfPlayer(matchesOf(matches, picked.group), picked.row.leaguePlayerId)}
          showPoints={usesPointSystem(picked.group)}
          scoring={picked.group.scoring}
          restingRunPoints={picked.group.restingRunPoints ?? null}
          onClose={() => setPicked(null)}
        />
      )}
    </section>
  );
};

export default RoundScoringPanel;
